import resourceInfinit from './resourceInfinit'

export default (opts = {}) => ({
  // Endless-scroll behaviour (prev/next/load/pull) is mixed in so the default table needs no
  // nested x-data wrapper — a nested component would shadow the selection dropdown's $refs.
  ...resourceInfinit(opts),

  open: false,
  dropdownTop: 0,
  dropdownLeft: 0,

  countAll: 0,
  countAllRelevant: 0,

  selected: [],
  // Explicit selection mode (for config selection_mode: 'toggle'). `selectionAlways` mirrors the
  // 'always' config (checkboxes always visible). "In selection mode" = checkboxes shown + rows
  // are selectable.
  selectionMode: false,
  selectionAlways: true,
  // Reactive mirror of "are all rows on the current page selected". Recomputed on selection
  // change AND after morphs (page switches), so the checkbox turns off when the new page is not
  // fully selected instead of staying stuck on the previous page's state.
  allOnPageSelected: false,
  // Reactive mirrors of the "all" / "all matching" checkbox states. Bound directly by the views
  // (x-bind on a data property updates reliably, unlike x-bind on a method call). The logic lives
  // in isSelectedAll() / isSelectedAllRelevant(); these are recomputed on every selection change.
  allSelected: false,
  allRelevantSelected: false,

  init() {
    this.selected = []

    // Capture the table root element so DOM-based selection helpers (getAllOnPage) work even
    // when called from a nested x-data scope, e.g. an app-provided mobile action bar where
    // `$root` would otherwise resolve to that nested component instead of the table.
    this._rootEl = this.$el;
    this.selectionAlways = this.$el?.dataset?.selectionMode !== 'toggle';

    this._rebindSticky();

    // Defer to next tick to ensure table has rendered sizes
    queueMicrotask(() => this.recomputeFixedAndMark());

    // Recompute on resize – store reference for cleanup
    this._resizeHandler = () => { this.recomputeFixedAndMark(); };
    window.addEventListener('resize', this._resizeHandler);

    // Store cleanup handle returned by Livewire.hook
    this._morphedCleanup = window.Livewire.hook('morphed', (component) => {
      // If our root was detached by a morph, make sure an open dropdown
      // does not keep living with stale global listeners.
      if (this.open && (!this.$root || !this.$root.isConnected)) {
        this._forceCloseDropdown();
      }

      if (this.$root && component?.el && this.$root.isConnected && (component.el === this.$root || component.el.contains(this.$root) || this.$root.contains(component.el))) {
        // Even when a query returns from A through B to A, an accepted table
        // morph starts a new list generation. A fragment requested for the
        // earlier A must never append to this freshly rendered A.
        this._invalidateEndlessRequests();
        queueMicrotask(() => {
          this._rebindSticky();
          // Rows/counts may have changed (e.g. page switch); re-evaluate the select-all states.
          this.refreshSelectionState();
        });
      }
    });

    // --- Out-of-order morph guard ----------------------------------------
    // Livewire v4 deliberately runs concurrent `wire:model.live` commits for
    // the same component (see coordinateNetworkInteractions). For a debounced
    // live search that means two requests can be in flight simultaneously and
    // their responses can arrive out of order: a slower older response (e.g.
    // search "se") morphs the unfiltered default list over the newer correct
    // result ("search"). We tag every user-initiated query change with a
    // monotonically increasing generation and discard any incoming morph of
    // the content wrapper that carries an older generation.
    this._gen = Number(
      this.$root?.querySelector?.('.resourcerer-table-content')?.getAttribute?.('data-client-gen')
    ) || 0;

    // Highest generation actually applied to the DOM. The skip decision is made
    // against THIS, not against `_gen`: `_gen` is driven by raw input/change
    // events and can race ahead of the generation the server has actually
    // echoed back (e.g. a trailing `change`/Enter event bumps `_gen` without
    // producing a new commit). Comparing against `_gen` would then misclassify
    // the correct newest response as stale and skip its morph, leaving the
    // table stuck on the previous result until a full page reload.
    this._appliedGen = this._gen;

    // Mark this response, before its morph, only when every action/update is
    // filter UI work. A refresh, selection update or action bundled with it
    // must retain the ordinary row morph. No global same-query suppression.
    this._filterUiCleanup = window.Livewire.hook('request', ({ options, succeed }) => {
      const id = this._rootEl.closest('[wire\\:id]')?.getAttribute('wire:id');
      const commit = JSON.parse(options.body).components
        .find(entry => JSON.parse(entry.snapshot).memo.id === id);
      const uiMethods = ['openFilterEditor', 'showFilterChooser', 'cancelFilterEditor', 'cancelFilterPanel',
        'applyFilterEditor', 'previewFilterResultCount', 'previewFilterPanelResultCount'];
      if (!commit?.calls.length || !commit.calls.every(call => uiMethods.includes(call.method))) return;
      if (!Object.keys(commit.updates).every(key => key === 'clientGen' || key === 'filterDraft' || key.startsWith('filterDraft.'))) return;
      succeed(({ json }) => {
        for (const payload of json.components) {
          if (!payload.effects?.html || JSON.parse(payload.snapshot).memo.id !== id) continue;
          const template = document.createElement('template');
          template.innerHTML = payload.effects.html;
          template.content.querySelectorAll('.resourcerer-table-content').forEach(content => {
            if (content.closest('[wire\\:id]')?.getAttribute('wire:id') === id) {
              content.setAttribute('data-filter-ui-render', 'true');
            }
          });
          payload.effects.html = template.innerHTML;
        }
      });
    });

    this._bumpGen = (event) => {
      const target = event?.target;
      if (target?.classList?.contains?.('model-selector')
        || target?.getAttribute?.('x-model') === 'selected') {
        // Row selection is Alpine-local and does not change the query or row
        // fragment shape. Do not cancel an in-flight endless page for it.
        return;
      }

      this._gen += 1;
      // Invalidate fragment work as soon as the user changes table input,
      // rather than waiting for the corresponding Livewire morph.
      this._invalidateEndlessRequests();
      // Deferred set (no extra request): rides the next live commit, so the
      // server echoes the value back onto the content wrapper.
      try { this.$wire?.set?.('clientGen', this._gen, false); } catch (e) { /* noop */ }
    };
    // Capture phase: record the intent even if a handler stops propagation.
    this.$root?.addEventListener?.('input', this._bumpGen, true);
    this.$root?.addEventListener?.('change', this._bumpGen, true);

    this._morphUpdatingCleanup = window.Livewire.hook('morph.updating', ({ el, toEl, skip }) => {
      if (toEl?.getAttribute?.('data-preserve-filter-ui-list') === 'true') {
        if (this.$root?.contains(el)) skip();
        return;
      }
      if (!el || !el.classList || !el.classList.contains('resourcerer-table-content')) return;
      if (!this.$root || !this.$root.contains || !this.$root.contains(el)) return;
      const incoming = Number(toEl?.getAttribute?.('data-client-gen'));
      if (!Number.isFinite(incoming)) return;
      // Discard only renders strictly older than what we have already applied.
      // This still drops genuinely out-of-order (slower, older) responses, but
      // never the newest correct one just because the local counter raced ahead.
      if (incoming < this._appliedGen) {
        skip();
        return;
      }
      this._appliedGen = incoming;
      if (toEl.getAttribute('data-filter-ui-render') === 'true') {
        this._preserveFilterUiList(toEl);
        toEl.removeAttribute('data-filter-ui-render');
      }
    });

    // Store bound references for dropdown listeners (toggle/close)
    this._boundUpdateDropdownPosition = this.updateDropdownPosition.bind(this);
    this._boundHandleClickOutside = this.handleClickOutside.bind(this);

    // bfcache / unload safety: if an open dropdown survives into a page hide
    // (browser-back, navigation), drop global listeners and reset state.
    this._boundPageHide = () => {
      if (this.open) this._forceCloseDropdown();
    };
    window.addEventListener('pagehide', this._boundPageHide);

    // Livewire navigate (back/forward + bfcache): force-close the dropdown
    // and drop global listeners before Alpine re-initialises the new page,
    // so no stale handler keeps firing against a dead scope.
    this._boundNavigate = () => {
      this._forceCloseDropdown();
    };
    window.addEventListener('livewire:navigate', this._boundNavigate);
    window.addEventListener('livewire:navigating', this._boundNavigate);

    // Renderless, targeted soft refresh: update only visible rows whose IDs changed
    this.$wire.on('resourcerer:soft-refresh', this.onSoftRefresh.bind(this))

    this.countAll = this.$wire.entangle('countAll'),
    this.countAllRelevant = this.$wire.entangle('countAllRelevant'),

    // Recompute all three select-all checkbox states whenever the selection changes.
    this.$watch('selected', () => this.refreshSelectionState())

    // x-show removes the selection column from layout in toggle mode. Wait for Alpine to
    // apply its display change before measuring the width of the fixed column group.
    this.$watch('inSelectionMode', () => this.$nextTick(() => {
      requestAnimationFrame(() => this.recomputeFixedAndMark());
    }));

    queueMicrotask(() => this.refreshSelectionState());
  },

  _preserveFilterUiList(incoming) {
    const list = this._listEl();
    const engine = list?.closest('[x-data]');
    const fingerprint = engine ? window.Alpine.$data(engine).queryFingerprint : null;
    if (typeof fingerprint !== 'string' || !fingerprint) return;

    // Use the existing query-keyed endless boundary, shared by stock and
    // nested layouts. A changed query has a different key and morphs normally.
    let boundary = list;
    while (boundary && boundary !== this.$root && !boundary.getAttribute('wire:key')?.includes(fingerprint)) {
      boundary = boundary.parentElement;
    }
    const key = boundary?.getAttribute('wire:key');
    if (!key?.includes(fingerprint)) return;
    const nextBoundary = Array.from(incoming.querySelectorAll('[wire\\:key]'))
      .find(element => element.getAttribute('wire:key') === key);
    const selector = list.hasAttribute('data-endless-items') ? '[data-endless-items]' : '[x-ref="list"]';
    // Stock empty bodies are not accumulated lists. Let both header and body
    // morph normally when either side changes between empty/nonempty branches.
    if (!list.matches(selector)) return;
    const nextList = nextBoundary?.querySelector(selector);
    if (nextList?.tagName !== list.tagName) return;
    nextList.setAttribute('data-preserve-filter-ui-list', 'true');
  },

  refreshAllOnPageSelected() {
    this.allOnPageSelected = this._allSelected(this.getAllOnPage());
  },

  // Recompute the three select-all checkbox mirrors from the current selection.
  refreshSelectionState() {
    this.refreshAllOnPageSelected();
    this.allSelected = this.isSelectedAll();
    this.allRelevantSelected = this.isSelectedAllRelevant();
  },

  // Read the current server counts. The entangled `this.count*` can be a stale snapshot after a
  // filter/search change, so prefer the live Livewire property and fall back to the local copy.
  _countAll() {
    return Number(this.$wire?.countAll ?? this.countAll ?? 0);
  },
  _countAllRelevant() {
    return Number(this.$wire?.countAllRelevant ?? this.countAllRelevant ?? 0);
  },

  // "All" is ticked only when the whole set is selected.
  isSelectedAll() {
    const total = this._countAll();
    return total > 0 && this.selected.length === total;
  },

  // "All matching" is ticked when the selection is exactly the matching set, OR covers everything
  // (the matching set is a subset of all) — so selecting "all" also ticks "all matching".
  isSelectedAllRelevant() {
    const total = this._countAllRelevant();
    return (total > 0 && this.selected.length === total) || this.isSelectedAll();
  },

  // Checkboxes visible + rows selectable: always (config 'always'), or once the mode is entered /
  // a selection exists (config 'toggle').
  get inSelectionMode() {
    return this.selectionAlways || this.selectionMode || this.selected.length > 0;
  },

  enableSelectionMode() {
    this.selectionMode = true;
  },

  disableSelectionMode() {
    this.selected = [];
    this.selectionMode = false;
  },

  destroy() {
    // Ensure an open dropdown does not leak global listeners past destroy.
    this._forceCloseDropdown();

    // Remove global resize listener
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
    }

    // Remove bfcache/unload safety listener
    if (this._boundPageHide) {
      window.removeEventListener('pagehide', this._boundPageHide);
      this._boundPageHide = null;
    }

    // Remove livewire navigate safety listener
    if (this._boundNavigate) {
      window.removeEventListener('livewire:navigate', this._boundNavigate);
      window.removeEventListener('livewire:navigating', this._boundNavigate);
      this._boundNavigate = null;
    }

    // Unhook Livewire morphed listener
    if (this._morphedCleanup) {
      this._morphedCleanup();
    }

    // Remove out-of-order morph guard (listeners + Livewire hook)
    if (this._bumpGen) {
      this.$root?.removeEventListener?.('input', this._bumpGen, true);
      this.$root?.removeEventListener?.('change', this._bumpGen, true);
    }
    if (this._morphUpdatingCleanup) {
      this._morphUpdatingCleanup();
    }
    this._filterUiCleanup?.();

    // Disconnect MutationObserver and scroll listener from sticky binding
    if (this._fixedObserver) {
      try { this._fixedObserver.disconnect(); } catch (e) {}
      this._fixedObserver = null;
    }
    if (this._scroller && this._scrollHandler) {
      // Match the options used in addEventListener (passive:true) so the listener
      // is reliably removed across browsers. The spec only requires `capture` to
      // match, but keeping the full options object avoids any ambiguity.
      this._scroller.removeEventListener('scroll', this._scrollHandler, { passive: true });
    }

    // Cancel any pending rAF
    if (this._markRaf) {
      cancelAnimationFrame(this._markRaf);
      this._markRaf = null;
    }
  },

  async onSoftRefresh(event) {
    try {
      const changed = (event.ids || []).map(String)
      const targetResource = event.resource || null
      const myResource = this.$root?.dataset?.resource || null

      if (!changed.length || !this.$root) return
      if (targetResource && myResource && targetResource !== myResource) return

      // Only consider rows under this table root. Use a Set for O(1) membership
      // checks so the intersection stays linear even with many changed ids.
      const changedSet = new Set(changed)
      const visible = Array.from(this.$root.querySelectorAll('[data-row-id]')).map(el => el.getAttribute('data-row-id'))
      const need = visible.filter(id => changedSet.has(id))
      if (!need.length) return

      const resp = await this.$wire.fetchRowsByIds(need)
      const tpl = document.createElement('template')
      tpl.innerHTML = (resp.html || '').trim()

      const newRows = tpl.content.querySelectorAll('[data-row-id]')
      if (newRows.length) {
        newRows.forEach((newEl) => {
          const id = newEl.getAttribute('data-row-id')
          // Prefer matching wrapper if present
          let current = this.$root.querySelector(`[data-row-id='${id}']`)
          if (current) {
            current.dispatchEvent(new CustomEvent('resourcerer:row-refreshing'))
            this.$morph(current, newEl)
          }
        })
        queueMicrotask(() => this._rebindSticky())
      }

      ;(resp.missing || []).forEach((id) => {
        const el = this.$root.querySelector(`[data-row-id='${id}']`)
        const row = el?.closest('.resourcerer-row') || el
        if (row) {
          el.dispatchEvent(new CustomEvent('resourcerer:row-refreshing'))
          row.remove()
        }
      })
    } catch (err) {
      console.error('[resourcerer:soft-refresh] patch failed', err)
    }
  },

  toggle() {
    if (this.open) {
      return this.close()
    }

    const button = this.$refs.button;
    // Guard against missing / detached button (e.g. after a morph): do not
    // open against a stale ref, never throw from a user click.
    if (!button || !button.isConnected) return;

    button.focus()

    this.open = true
    this.updateDropdownPosition();

    // If updateDropdownPosition detected a stale/detached ref it has already
    // force-closed; in that case we must not register global listeners.
    if (!this.open) return;

    this._registerDropdownListeners();
  },

  close(focusAfter) {
    if (! this.open) return

    this.open = false

    this._cleanupDropdownListeners();

    focusAfter && focusAfter.focus()
  },
  updateDropdownPosition() {
    if (!this.open) return;

    const button = this.$refs.button;
    if (!button || !button.isConnected) {
      // Stale/detached ref after morph: drop state + global listeners,
      // but never throw out of a scroll/resize handler.
      this._forceCloseDropdown();
      return;
    }

    // Get input element dimensions and position
    const inputRect = button.getBoundingClientRect();

    // Calculate dropdown position
    this.dropdownTop = inputRect.bottom; // Bottom of input
    this.dropdownLeft = inputRect.left; // Left of input
  },

  // Idempotent registration for dropdown global listeners.
  _registerDropdownListeners() {
    if (this._dropdownListenersActive) return;
    window.addEventListener('scroll', this._boundUpdateDropdownPosition, true);
    window.addEventListener('resize', this._boundUpdateDropdownPosition);
    document.addEventListener('click', this._boundHandleClickOutside);
    this._dropdownListenersActive = true;
  },

  // Idempotent removal for dropdown global listeners.
  _cleanupDropdownListeners() {
    if (!this._dropdownListenersActive) return;
    window.removeEventListener('scroll', this._boundUpdateDropdownPosition, true);
    window.removeEventListener('resize', this._boundUpdateDropdownPosition);
    document.removeEventListener('click', this._boundHandleClickOutside);
    this._dropdownListenersActive = false;
  },

  // Close the dropdown unconditionally and drop global listeners. Safe to
  // call from morph/pagehide/stale-ref paths where `this.open` may or may
  // not still reflect a usable UI state.
  _forceCloseDropdown() {
    this.open = false;
    this._cleanupDropdownListeners();
  },
  handleClickOutside(event) {
    // Check if the click was inside the container or the teleported dropdown
    const clickedInsideContainer = this.$refs.container?.contains(event.target);
    const clickedInsideDropdown = this.$refs.panel?.contains(event.target);

    if (!clickedInsideContainer && !clickedInsideDropdown) {
      this.close();
    }
  },

  // True when at least one key is given AND all of them are selected. Used to toggle the
  // select-all actions against the ACTUAL selection instead of a derived flag (reliable regardless
  // of count/flag timing).
  _allSelected(keys) {
    return keys.length > 0 && this.areSelected(keys)
  },

  async selectAll() {
    const keys = await this.$wire.getAllRecordKeys()
    this._allSelected(keys) ? this.deselect(keys) : this.select(keys)
  },
  async selectAllRelevant() {
    const keys = await this.$wire.getAllRelevantRecordKeys()
    this._allSelected(keys) ? this.deselect(keys) : this.select(keys)
  },
  selectAllOnPage() {
    const allOnPage = this.getAllOnPage()
    this._allSelected(allOnPage) ? this.deselect(allOnPage) : this.select(allOnPage)
  },

  select(keys) {
    // Reassign (not push) so the `selected` watcher and every x-model checkbox update reliably in
    // one go instead of relying on in-place mutation tracking.
    const set = new Set(this.selected)
    for (const key of keys) {
      set.add(key)
    }
    this.selected = [...set]
  },

  deselect: function (keys) {
    const remove = new Set(keys)
    this.selected = this.selected.filter((key) => !remove.has(key))
  },

  areSelected: function (keys) {
    // Set-based membership so callers with large selections stay O(keys). Note: `areSelected([])`
    // is vacuously true — callers that care about empty input guard via `keys.length` (see
    // `_allSelected()` / `refreshAllOnPageSelected()`).
    const set = new Set(this.selected)
    return keys.every((key) => set.has(key))
  },

  getAllOnPage: function () {
    const keys = []
    const elements = (this._rootEl ?? this.$root)?.getElementsByClassName?.('model-selector') ?? [];

    for (let el of elements) {
      keys.push(el.value)
    }

    return keys
  },

  // Recompute sticky positions and immediately update the left and right edge markers
  recomputeFixedAndMark() {
    this.computeFixedColumns();
    queueMicrotask(() => {
      this._syncStickyLayers(true);
      this.doMarkLastAttachedFixedColumn(true);
      this.doMarkFirstAttachedFixedRightColumn(true);
    });
  },

  // Computes sticky left offsets for fixed columns and right offsets for right-fixed columns
  computeFixedColumns() {
    const root = this.$root;
    if (!root) { return; }

    // Reset previous inline styles
    root.querySelectorAll('.resource-table-column-fixed').forEach((el) => {
      el.style.left = '';
    });
    root.querySelectorAll('.resource-table-column-fixed-right').forEach((el) => {
      el.style.right = '';
    });

    const headerRow = root.querySelector('thead tr');
    if (!headerRow) { return; }

    let runningLeft = 0;

    // First, if there is a fixed selection column, handle it explicitly
    const selectHeader = headerRow.querySelector('th.resource-table-column-fixed[data-field-name="__select__"]');
    if (selectHeader) {
      const rect = selectHeader.getBoundingClientRect();
      selectHeader.style.left = `${runningLeft}px`;
      root.querySelectorAll('tbody tr td.resource-table-column-fixed[data-field-name="__select__"]').forEach((td) => {
        td.style.left = `${runningLeft}px`;
      });
      runningLeft += Math.round(rect.width);
    }

    // Now process each fixed field column in the order they appear in the header
    const fixedHeaders = headerRow.querySelectorAll('th.resource-table-column-fixed[data-field-name]:not([data-field-name="__select__"])');
    fixedHeaders.forEach((th) => {
      const name = th.getAttribute('data-field-name');
      const rect = th.getBoundingClientRect();
      th.style.left = `${runningLeft}px`;
      // Match corresponding body tds by data-field-name
      root.querySelectorAll(`tbody tr td.resource-table-column-fixed[data-field-name="${name}"]`).forEach((td) => {
        td.style.left = `${runningLeft}px`;
      });
      runningLeft += Math.round(rect.width);
    });

    // Right-fixed columns stack from the right edge inwards: the outermost sits at 0, each one
    // further in is offset by the widths of those outside it. A header that is not rendered
    // (mobile card layout) pins nothing, so its cells keep their stylesheet position.
    let runningRight = 0;
    Array.from(headerRow.querySelectorAll('th.resource-table-column-fixed-right[data-field-name]'))
      .reverse()
      .forEach((th) => {
        if (!th.getClientRects().length) { return; }
        const name = th.getAttribute('data-field-name');
        th.style.right = `${runningRight}px`;
        root.querySelectorAll(`tbody tr td.resource-table-column-fixed-right[data-field-name="${name}"]`).forEach((td) => {
          td.style.right = `${runningRight}px`;
        });
        runningRight += Math.round(th.getBoundingClientRect().width);
      });
  },

  // Coalesce rapid calls using rAF for last-sticky marking (left and right edge)
  scheduleMarkLastAttachedFixedColumn() {
    if (this._markRaf) { return; }
    this._markRaf = requestAnimationFrame(() => {
      this._markRaf = null;
      this._syncStickyLayers();
      this.doMarkLastAttachedFixedColumn();
      this.doMarkFirstAttachedFixedRightColumn();
    });
  },
  // A sticky cell participates in stacking only after it leaves its natural position.
  // Otherwise a right-fixed field that has scrolled back into the row can cover left-fixed cells.
  _syncStickyLayers(refreshCells = false) {
    const root = this.$root;
    const scroller = this._getScroller();
    const headerRow = this._getHeaderRow();
    if (!root || !scroller || !headerRow) { return; }

    const naturalLeftMap = this._computeNaturalLeftMap(headerRow);
    const visibleRight = scroller.scrollLeft + scroller.clientWidth;
    const sync = (th, edge, stuck) => {
      const wasStuck = th.classList.contains('resource-table-column-stuck');
      if (wasStuck === stuck && !refreshCells) { return; }
      th.classList.toggle('resource-table-column-stuck', stuck);
      const name = th.getAttribute('data-field-name');
      root.querySelectorAll(`tbody tr td.${edge}[data-field-name="${name}"]`).forEach((td) => {
        td.classList.toggle('resource-table-column-stuck', stuck);
      });
    };

    headerRow.querySelectorAll('th.resource-table-column-fixed[data-field-name]').forEach((th) => {
      const left = parseFloat(th.style.left || '0') || 0;
      const naturalLeft = naturalLeftMap.get(th) ?? 0;
      const stuck = !!th.getClientRects().length && scroller.scrollLeft > naturalLeft - left + 2;
      sync(th, 'resource-table-column-fixed', stuck);
    });
    headerRow.querySelectorAll('th.resource-table-column-fixed-right[data-field-name]').forEach((th) => {
      const right = parseFloat(th.style.right || '0') || 0;
      const naturalRight = (naturalLeftMap.get(th) ?? 0) + Math.round(th.getBoundingClientRect().width);
      const stuck = !!th.getClientRects().length && naturalRight > visibleRight - right + 2;
      sync(th, 'resource-table-column-fixed-right', stuck);
    });
  },

  // Right-edge counterpart of doMarkLastAttachedFixedColumn(): mark the innermost right-fixed
  // column that is actually stuck, i.e. whose natural position lies beyond the visible right
  // edge. Scrolled fully to the right (or nothing to scroll) -> no marker.
  doMarkFirstAttachedFixedRightColumn(refreshCells = false) {
    const scroller = this._getScroller();
    const headerRow = this._getHeaderRow();
    if (!scroller || !headerRow) { return; }

    const tolerance = 2; // handle subpixel/zoom rounding
    const visibleRight = scroller.scrollLeft + scroller.clientWidth;
    const naturalLeftMap = this._computeNaturalLeftMap(headerRow);

    let candidate = null;
    let candidateEdge = -Infinity;

    headerRow.querySelectorAll('th.resource-table-column-fixed-right[data-field-name]').forEach((th) => {
      if (!th.getClientRects().length) { return; }
      const stickyRight = parseFloat(th.style.right || '0') || 0;
      const naturalRight = (naturalLeftMap.get(th) ?? 0) + Math.round(th.getBoundingClientRect().width);
      if (naturalRight > visibleRight - stickyRight + tolerance && stickyRight >= candidateEdge) {
        candidateEdge = stickyRight;
        candidate = th;
      }
    });

    const name = candidate?.getAttribute('data-field-name') ?? null;

    // If the target didn't change, avoid toggling classes to prevent unnecessary mutations
    if (this._firstStickyRightName === name) {
      if (refreshCells && candidate) {
        candidate.classList.add('first-sticky-right');
        this.$root?.querySelectorAll(`tbody tr td.resource-table-column-fixed-right[data-field-name="${name}"]`)?.forEach((td) => td.classList.add('first-sticky-right'));
      }
      return;
    }

    this._clearFirstStickyRight();
    if (!candidate) { return; }

    candidate.classList.add('first-sticky-right');
    this.$root?.querySelectorAll(`tbody tr td.resource-table-column-fixed-right[data-field-name="${name}"]`)?.forEach((td) => td.classList.add('first-sticky-right'));
    this._firstStickyRightName = name;
  },

  // Determine which fixed column should display the end-of-fixed-group separator.
  // New rule: if not scrolled -> no marker; if scrolled -> mark the last actually stuck fixed column (max left where left <= scrollLeft + tolerance).
  doMarkLastAttachedFixedColumn(refreshCells = false) {
    const root = this.$root;
    if (!root) { return; }

    const scroller = this._getScroller();
    const headerRow = this._getHeaderRow();
    if (!scroller || !headerRow) { return; }

    const scrollLeft = scroller.scrollLeft; // preserve precision
    const tolerance = 2; // handle subpixel/zoom rounding

    // At rest: no marker
    if (scrollLeft <= tolerance) {
      // Clear any existing markers and remember no active name
      this._clearLastSticky();
      return;
    }

    // Build ordered headers list: selection then others
    const headers = this._getFixedHeadersOrdered(headerRow);
    if (!headers.length) { return; }

    // Choose the last actually stuck: the fixed column whose sticky edge has reached the left boundary.
    // A column is "actually stuck" if its visual left (sticky left) is at or past the scroller's left edge.
    // This occurs when: scroller.scrollLeft >= (columnNaturalLeft - stickyLeft)
    // where stickyLeft is the inline left we assign, and columnNaturalLeft is the column's natural offset from table start.
    let candidate = null;
    let candidateEdge = -Infinity;

    // Build natural offsets for all header cells by summing their widths in document order
    const naturalLeftMap = this._computeNaturalLeftMap(headerRow);

    headers.forEach((th) => {
      const stickyLeft = parseFloat((th.style.left || '0').replace('px','')) || 0;
      const naturalLeft = naturalLeftMap.get(th) ?? 0;
      const neededScrollToStick = Math.max(0, naturalLeft - stickyLeft);
      if (scrollLeft + tolerance >= neededScrollToStick) {
        const edge = stickyLeft;
        if (edge >= candidateEdge) {
          candidateEdge = edge;
          candidate = th;
        }
      }
    });

    if (!candidate) {
      // Nothing qualifies as stuck; clear and exit
      if (this._lastStickyName) {
        root.querySelectorAll('.resource-table-column-fixed.last-sticky').forEach((el) => el.classList.remove('last-sticky'));
        this._lastStickyName = null;
      }
      return;
    }

    const name = candidate.getAttribute('data-field-name');

    // If the target didn't change, avoid toggling classes to prevent unnecessary mutations
    if (this._lastStickyName === name) {
      if (refreshCells) {
        candidate.classList.add('last-sticky');
        root.querySelectorAll(`tbody tr td.resource-table-column-fixed[data-field-name="${name}"]`).forEach((td) => td.classList.add('last-sticky'));
      }
      return;
    }

    this._markStickyByName(name, candidate);
  },

  // Small helpers for clarity
  _getHeaderRow() {
    return this.$root?.querySelector('thead tr') || null;
  },
  _getScroller() {
    return this.$root?.querySelector('.overflow-x-auto') || null;
  },
  _getFixedHeadersOrdered(headerRow) {
    const headers = [];
    const selectHeader = headerRow.querySelector('th.resource-table-column-fixed[data-field-name="__select__"]');
    if (selectHeader) { headers.push(selectHeader); }
    headerRow.querySelectorAll('th.resource-table-column-fixed[data-field-name]:not([data-field-name="__select__"])')
      .forEach((th) => headers.push(th));
    return headers;
  },
  _computeNaturalLeftMap(headerRow) {
    const allHeaders = Array.from(headerRow.querySelectorAll('th'));
    const naturalLeftMap = new Map();
    let naturalRunning = 0;
    allHeaders.forEach((h) => {
      naturalLeftMap.set(h, naturalRunning);
      const w = Math.round(h.getBoundingClientRect().width);
      naturalRunning += w;
    });
    return naturalLeftMap;
  },
  _clearLastSticky() {
    this.$root?.querySelectorAll('.resource-table-column-fixed.last-sticky').forEach((el) => el.classList.remove('last-sticky'));
    this._lastStickyName = null;
  },
  _clearFirstStickyRight() {
    this.$root?.querySelectorAll('.resource-table-column-fixed-right.first-sticky-right').forEach((el) => el.classList.remove('first-sticky-right'));
    this._firstStickyRightName = null;
  },
  _markStickyByName(name, candidate) {
    // Clear previous markers
    this.$root?.querySelectorAll('.resource-table-column-fixed.last-sticky').forEach((el) => el.classList.remove('last-sticky'));
    // Mark header
    candidate.classList.add('last-sticky');
    // Mark corresponding body cells
    this.$root?.querySelectorAll(`tbody tr td.resource-table-column-fixed[data-field-name="${name}"]`)?.forEach((td) => td.classList.add('last-sticky'));
    this._lastStickyName = name;
  },

  _rebindSticky() {
    this._clearLastSticky();
    this._clearFirstStickyRight();

    // Clean up old observer and scroll listener if any
    if (this._fixedObserver) {
      try { this._fixedObserver.disconnect(); } catch (e) {}
      this._fixedObserver = null;
    }
    if (this._scroller && this._scrollHandler) {
      this._scroller.removeEventListener('scroll', this._scrollHandler, { passive: true });
    }

    // Observe DOM changes inside the table to recalc stickies (e.g., pagination, sorting)
    const table = this.$root?.querySelector('table');
    if (table && window.MutationObserver) {
      const observer = new MutationObserver(() => queueMicrotask(() => this.recomputeFixedAndMark()));
      observer.observe(table, { childList: true, subtree: true, attributes: false });
      this._fixedObserver = observer;
    }
    // Update on horizontal scroll to move the last-sticky border
    const nextScroller = this.$root?.querySelector('.overflow-x-auto') || null;
    if (nextScroller) {
      this._scroller = nextScroller;
      this._scrollHandler = () => this.scheduleMarkLastAttachedFixedColumn();
      this._scroller.addEventListener('scroll', this._scrollHandler, { passive: true });
    } else {
      // Keep state consistent: no scroller -> no handler
      this._scroller = null;
      this._scrollHandler = null;
    }

    // Ensure marker is present immediately after rebind (no extra scroll needed)
    queueMicrotask(() => this.recomputeFixedAndMark());
  },
})
